import { Injector, computed, inject, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField, CommonTextFieldConfig, Option } from "@eforall/common";
import { AccGoalFormValues, AccGoalStatusType, DialogMode } from "@interfaces";
import { DomainDataService } from "../../../../../services";
import { GoalEditorDialogData } from "../goal-dialog-data";
import { GoalEditorDialogDataService } from "./goal-editor-dialog-data.service";


export class GoalEditorForm {

	private readonly domainDataService = inject(DomainDataService);
	private readonly dialogDataService = inject(GoalEditorDialogDataService);
	public readonly form = new CommonForm<AccGoalFormValues>(inject(Injector));

	private dialogData = signal<GoalEditorDialogData | undefined>(undefined);


	setDialogData(data: GoalEditorDialogData) {
		this.dialogData.set(data);
	}


	private readonly ventureOptions = computed<Option<number>[]>(() => {
		const data = this.dialogData();
		if (!data) return [];

		if (data.mode === DialogMode.ADD) {
			return data.ventures.reduce((a: Option<number>[], venture) => {
				a.push({
					value: venture.ventureId,
					text: venture.ventureName,
				});
				return a;
			}, []);
		}

		return [{ value: this.dialogDataService.accGoalFormValues()?.accVentureId ?? 0, text: data.ventureName }];

	});
	readonly ventureId = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Venture',
			isRequired: true,
			options: this.ventureOptions(),
			isEnabled: computed(() => this.dialogData()?.mode === DialogMode.ADD),
		}),
		formFieldName: 'accVentureId',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.accGoalFormValues()?.accVentureId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	readonly summary = computed<CommonTextField>(() => ({
		config: signal<CommonTextFieldConfig>({
			label: 'Summary',
			isRequired: true,
			min: 3,
			max: 60,
			fixCase: 'sentence-case',
			multiLine: false,
		}),
		formFieldName: 'summary',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.accGoalFormValues()?.summary ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	private readonly statusOptions = computed<{ value: AccGoalStatusType, text: string }[]>(() => {
		return [
			{ value: 'Not Started', text: 'Not Started' },
			{ value: 'In Progress', text: 'In Progress' },
			{ value: 'Abandoned', text: 'Abandoned' },
			{ value: 'Achieved', text: 'Achieved' },
		]
	});
	readonly status = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Status',
			isRequired: true,
			options: this.statusOptions(),
		}),
		formFieldName: 'status',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.accGoalFormValues()?.status),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	private readonly categoryOptions = computed(() => {
		return this.domainDataService.data().accGoalCategories.reduce((a: Option<number>[], type) => {
			a.push({
				value: type.accGoalCategoryId,
				text: type.name,
			});
			return a;
		}, []);
	});
	readonly categoryId = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Category',
			isRequired: true,
			options: this.categoryOptions(),
		}),
		formFieldName: 'accGoalCategoryId',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.accGoalFormValues()?.accGoalCategoryId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	readonly details = computed<CommonTextField>(() => ({
		config: signal<CommonTextFieldConfig>({
			label: 'Details',
			isRequired: true,
			min: 1,
			max: 2000,
			fixCase: 'sentence-case',
			multiLine: true,
		}),
		formFieldName: 'details',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.accGoalFormValues()?.details ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


}